import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  type Timestamp,
} from 'firebase/firestore'
import { getDb, isFirebaseChatEnabled } from '@services/firebase'
import { useChatStore } from '@store/chatStore'
import { chatApi } from '@api/endpoints/chat'

/**
 * Global Firestore listener over the user's conversations — the SignalR ConversationUpdateSignal
 * replacement for everything except new-message delivery (which arrives via the per-conversation
 * message-doc listener, useFirestoreChat). Mount once app-wide (MainLayout).
 *
 * On any conversation change it refreshes the conversation list (sidebar previews + unread badges).
 * When a conversation's stateBumpAt advances (read receipts, reactions, participant add/leave) and
 * that conversation is active, it refetches its messages from REST so read/reaction/participant
 * state stays correct. New messages don't bump stateBumpAt, so they never trigger a refetch here.
 * No-op unless Firebase chat is enabled.
 */
export function useFirestoreConversations(userId: string | undefined) {
  const queryClient = useQueryClient()
  // Last-seen stateBump per conversation, so we refetch only on genuine state changes.
  const lastBump = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    if (!isFirebaseChatEnabled || !userId) return
    const db = getDb()
    if (!db) return

    lastBump.current.clear()
    const q = query(
      collection(db, 'conversations'),
      where('participantIds', 'array-contains', userId),
      limit(200)
    )

    let primed = false
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const initial = !primed
        let listChanged = false

        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') return
          const data = change.doc.data()
          const bump = (data.stateBumpAt as Timestamp | null)?.toMillis?.() ?? -1
          const id = change.doc.id

          if (initial) {
            // Seed baselines; don't react to the existing backlog.
            lastBump.current.set(id, bump)
            return
          }

          listChanged = true
          const prev = lastBump.current.get(id)
          lastBump.current.set(id, bump)

          // A genuine state change (not just a new-message lastMessageAt update) on the active
          // conversation → refetch its messages so reactions/reads/participants stay correct.
          if (prev !== undefined && prev !== bump && bump !== -1) {
            const activeId = useChatStore.getState().activeConversationId
            if (id === activeId) {
              chatApi
                .getMessages(activeId, 1, 50)
                .then((msgs) => {
                  const messages = Array.isArray(msgs) ? [...msgs].reverse() : []
                  useChatStore.getState().setMessages(activeId, messages, 1, false)
                })
                .catch(() => {})
            }
          }
        })

        primed = true
        if (listChanged) {
          queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
        }
      },
      (err) => console.warn('[firebase] conversations snapshot error', err)
    )

    return () => unsubscribe()
  }, [userId, queryClient])
}
